import React, { useState } from 'react';
import api from '../../utils/api';
import ClientCoreInfo from './ClientCoreInfo';
import ClientCustomFields from './ClientCustomFields';
import ClientAssociations from './ClientAssociations';

const ClientInfoTab = ({ client, onClientUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: client?.name || '',
    dba: client?.dba || '',
    client_code: client?.client_code || '',
    folderLink: client?.folderLink || '',
    is_active: client?.is_active !== undefined ? client.is_active : true,
    is_archived: client?.is_archived !== undefined ? client.is_archived : false,
    address_line1: client?.address_line1 || '',
    address_line2: client?.address_line2 || '',
    city: client?.city || '',
    state: client?.state || '',
    zip_code: client?.zip_code || '',
    website: client?.website || '',
    industry_type_id: client?.industry_type_id || '',
    custom_fields: client?.custom_fields || {}
  });

  // Update formData when client prop changes
  React.useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        dba: client.dba || '',
        client_code: client.client_code || '',
        folderLink: client.folderLink || '',
        is_active: client.is_active !== undefined ? client.is_active : true,
        is_archived: client.is_archived !== undefined ? client.is_archived : false,
        address_line1: client.address_line1 || '',
        address_line2: client.address_line2 || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
        website: client.website || '',
        industry_type_id: client.industry_type_id || '',
        custom_fields: client.custom_fields || {}
      });
    }
  }, [client]);

  // Handle field changes from section components
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Handle custom fields changes
  const handleCustomFieldsChange = (customFieldValues) => {
    setFormData(prev => ({ ...prev, custom_fields: customFieldValues }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      console.log('Sending update request with data:', formData);
      
      const response = await api.put(`/clients/${client.id}`, {
        name: formData.name,
        dba: formData.dba,
        client_code: formData.client_code,
        folderLink: formData.folderLink,
        is_active: formData.is_active,
        is_archived: formData.is_archived,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        website: formData.website,
        industry_type_id: formData.industry_type_id,
        custom_fields: formData.custom_fields
      });
      
      console.log('Update response:', response.data);
      
      // Update the client state with the response data
      if (response.data && response.data.client) {
        const updatedClientData = response.data.client;
        
        // Preserve existing projects and call parent update
        onClientUpdate({
          ...client,
          ...updatedClientData,
          projects: client.projects || []
        });
        
        setIsEditing(false);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error updating client:', err);
      setError('Failed to update client. Please try again. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle client active status
  const toggleActiveStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If client is archived, it cannot be activated
      if (client.is_archived && !client.is_active) {
        setError('Archived clients cannot be activated. Please unarchive the client first.');
        setLoading(false);
        return;
      }
      
      const newStatus = !client.is_active;
      
      const response = await api.patch(`/clients/${client.id}/toggle-active`, {
        is_active: newStatus
      });
      
      if (response.data && response.data.client) {
        const updatedClientData = response.data.client;
        
        // Update parent component and form data
        onClientUpdate({
          ...client,
          ...updatedClientData,
          projects: client.projects || []
        });
        
        setFormData({
          ...formData,
          is_active: updatedClientData.is_active
        });
      }
    } catch (err) {
      console.error('Error toggling client active status:', err);
      setError('Failed to update client status. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle client archived status
  const toggleArchivedStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newStatus = !client.is_archived;
      
      const response = await api.patch(`/clients/${client.id}/toggle-archived`, {
        is_archived: newStatus
      });
      
      if (response.data && response.data.client) {
        const updatedClientData = response.data.client;
        
        // Update parent component and form data
        onClientUpdate({
          ...client,
          ...updatedClientData,
          projects: client.projects || []
        });
        
        setFormData({
          ...formData,
          is_active: updatedClientData.is_active,
          is_archived: updatedClientData.is_archived
        });
      }
    } catch (err) {
      console.error('Error toggling client archived status:', err);
      setError('Failed to update client archived status. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      // Reset form data to original client data if canceling edit
      setFormData({
        name: client.name || '',
        dba: client.dba || '',
        folderLink: client.folderLink || '',
        is_active: client.is_active !== undefined ? client.is_active : true,
        is_archived: client.is_archived !== undefined ? client.is_archived : false,
        address_line1: client.address_line1 || '',
        address_line2: client.address_line2 || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
        website: client.website || '',
        industry_type_id: client.industry_type_id || '',
        custom_fields: client.custom_fields || {}
      });
      setError(null);
    }
    setIsEditing(!isEditing);
  };

  if (!client) {
    return <div>Loading client information...</div>;
  }

  return (
    <div className="mt-6 space-y-6">
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md border-l-4 border-red-500">
          <p>{error}</p>
        </div>
      )}
      
      {/* Header with Edit Controls - Enhanced with Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-indigo-50">
          <div>
            <h3 className="text-lg leading-6 font-medium text-indigo-900">Client Information</h3>
            <p className="mt-1 max-w-2xl text-sm text-indigo-700">Manage all client details and settings.</p>
          </div>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={toggleEditMode}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={toggleEditMode}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-indigo-300 shadow-sm text-sm font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={toggleActiveStatus}
                  disabled={client.is_archived || loading}
                  className={`inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm 
                    ${client.is_active 
                      ? 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200'} 
                    ${client.is_archived || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {client.is_active ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  type="button"
                  onClick={toggleArchivedStatus}
                  disabled={loading}
                  className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded shadow-sm 
                    ${client.is_archived 
                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'}
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {client.is_archived ? 'Unarchive' : 'Archive'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Core Account Information Section - Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50">
          <h3 className="text-lg leading-6 font-medium text-indigo-900">Core Account Information</h3>
          <p className="mt-1 max-w-2xl text-sm text-indigo-700">Essential client details and business information.</p>
        </div>
        <div className="border-t border-indigo-100">
          <ClientCoreInfo
            client={client}
            formData={formData}
            isEditing={isEditing}
            loading={loading}
            onFieldChange={handleFieldChange}
          />
        </div>
      </div>

      {/* Custom Fields Section - Blue Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-blue-500">
        <div className="px-4 py-5 sm:px-6 bg-blue-50">
          <h3 className="text-lg leading-6 font-medium text-blue-900">Custom Fields</h3>
          <p className="mt-1 max-w-2xl text-sm text-blue-700">Additional client information and custom data fields.</p>
        </div>
        <div className="border-t border-blue-100 px-4 py-5 sm:px-6">
          <ClientCustomFields
            clientId={client.id}
            customFieldValues={formData.custom_fields}
            onCustomFieldsChange={handleCustomFieldsChange}
            readOnly={!isEditing}
          />
        </div>
      </div>

      {/* Client Associations Section - Green Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-green-500">
        <div className="px-4 py-5 sm:px-6 bg-green-50">
          <h3 className="text-lg leading-6 font-medium text-green-900">Related Companies</h3>
          <p className="mt-1 max-w-2xl text-sm text-green-700">Manage relationships with other companies and business entities.</p>
        </div>
        <div className="border-t border-green-100 px-4 py-5 sm:px-6">
          <ClientAssociations
            clientId={client.id}
          />
        </div>
      </div>

      {/* Future sections will go here with additional color themes */}
      {/* 
      Financial Information - Purple Theme:
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-purple-500">
        <div className="px-4 py-5 sm:px-6 bg-purple-50">
          <h3 className="text-lg leading-6 font-medium text-purple-900">Financial Information</h3>
          <p className="mt-1 max-w-2xl text-sm text-purple-700">Billing details and financial settings.</p>
        </div>
      </div>
      
      Contract Information - Orange Theme:
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-orange-500">
        <div className="px-4 py-5 sm:px-6 bg-orange-50">
          <h3 className="text-lg leading-6 font-medium text-orange-900">Contract Information</h3>
          <p className="mt-1 max-w-2xl text-sm text-orange-700">Contract terms and agreement details.</p>
        </div>
      </div>
      */}
    </div>
  );
};

export default ClientInfoTab; 